import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Mic, MicOff, Video as VideoIcon, VideoOff, PhoneOff, MessageSquare, MessageSquareOff, Globe, X, Share2, Copy, Check, BookmarkPlus, BookmarkCheck } from 'lucide-react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Supported languages for translation
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hi', name: 'Hindi' },
];

const VideoCallPage = ({ user }) => {
  const { channelName } = useParams();
  const navigate = useNavigate();
  
  // Agora clients
  const [agoraClient, setAgoraClient] = useState(null);
  
  // Tracks
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState({});
  
  // UI State
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [isJoined, setIsJoined] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [connectionError, setConnectionError] = useState(null);
  
  // Translation
  const [translateEnabled, setTranslateEnabled] = useState(false);
  const [myLanguage, setMyLanguage] = useState('es');
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  
  // Captions
  const [theirCaption, setTheirCaption] = useState({ 
    text: '', words: [], currentWordIndex: -1, speaker: '', isInterim: true, translated: ''
  });
  const [captionHistory, setCaptionHistory] = useState([]);
  
  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const recognitionRef = useRef(null);
  const lastSentRef = useRef(0);
  const wordIndexRef = useRef(0);
  const [linkCopied, setLinkCopied] = useState(false);

  // Conversation saving
  const [isSaving, setIsSaving] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const transcriptBufferRef = useRef([]);
  const flushTimerRef = useRef(null);
  
  // WebSocket for captions (replaces RTM)
  const wsRef = useRef(null);
  const translateEnabledRef = useRef(false);
  const myLanguageRef = useRef('es');
  const isSavingRef = useRef(false);

  // Plan & guest limits
  const [planInfo, setPlanInfo] = useState({ plan_type: 'basic', max_guests: 1 });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Calculate if remote user exists
  const hasRemoteUser = Object.keys(remoteUsers).length > 0;

  // Keep refs in sync with state
  useEffect(() => { translateEnabledRef.current = translateEnabled; }, [translateEnabled]);
  useEffect(() => { myLanguageRef.current = myLanguage; }, [myLanguage]);
  useEffect(() => { isSavingRef.current = isSaving; }, [isSaving]);

  // Play local video - single stable container, no timeout race conditions
  useEffect(() => {
    if (localVideoTrack && localVideoRef.current && isJoined) {
      console.log('🎥 [Kaplie] Playing local video track...');
      try {
        localVideoTrack.play(localVideoRef.current, { fit: 'cover', mirror: true });
        console.log('✅ [Kaplie] Local video playing');
      } catch (e) {
        console.error('❌ [Kaplie] Failed to play video:', e);
      }
    }
  }, [localVideoTrack, isJoined]);

  // Fetch plan info for guest limits
  useEffect(() => {
    const fetchPlan = async () => {
      const token = localStorage.getItem('kaplie_token');
      if (!token) return;
      try {
        const res = await axios.get(`${API}/stripe/plan-info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPlanInfo(res.data);
      } catch (e) {}
    };
    fetchPlan();
  }, []);

  // Initialize call
  useEffect(() => {
    initializeCall();
    return () => cleanup();
  }, []);

  const initializeCall = async () => {
    console.log('🚀 [Kaplie] Starting call initialization...');
    setConnectionError(null);
    
    try {
      setIsConnecting(true);
      
      // Step 1: Check for auth token (guests can join without one)
      const token = localStorage.getItem('kaplie_token');
      const isGuest = !token;
      if (isGuest) {
        console.log('👤 [Kaplie] Guest user joining call');
      } else {
        console.log('✅ [Kaplie] Auth token found');
      }
      
      // Step 2: Get Agora credentials from backend
      console.log('📡 [Kaplie] Fetching Agora token...');
      let agoraToken, appId;
      try {
        const endpoint = isGuest ? `${API}/agora/guest-token` : `${API}/agora/token`;
        const headers = isGuest 
          ? { 'Content-Type': 'application/json' } 
          : { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        const response = await axios.post(
          endpoint,
          { channel_name: channelName, uid: 0 },
          { headers }
        );
        agoraToken = response.data.token;
        appId = response.data.app_id;
        console.log('✅ [Kaplie] Agora credentials received', { appId: appId?.substring(0, 8) + '...', channel: channelName });
      } catch (e) {
        console.error('❌ [Kaplie] Failed to get Agora token:', e.response?.data || e.message);
        // If auth token expired, fall back to guest token
        if (!isGuest && e.response?.status === 401) {
          console.log('🔄 [Kaplie] Auth expired, retrying as guest...');
          try {
            const guestResp = await axios.post(
              `${API}/agora/guest-token`,
              { channel_name: channelName, uid: 0 }
            );
            agoraToken = guestResp.data.token;
            appId = guestResp.data.app_id;
          } catch (e2) {
            setConnectionError('Failed to connect. Please check your internet connection.');
            setIsConnecting(false);
            return;
          }
        } else {
          setConnectionError('Failed to get call credentials. Please check your internet connection.');
          toast.error('Failed to connect. Please try again.');
          setIsConnecting(false);
          return;
        }
      }
      
      if (!appId) {
        console.error('❌ [Kaplie] Agora App ID is missing');
        setConnectionError('Video service not configured.');
        toast.error('Video service not configured. Contact support.');
        setIsConnecting(false);
        return;
      }
      
      // Step 3: Request camera/microphone permissions
      console.log('🎤 [Kaplie] Requesting media permissions...');
      try {
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        console.log('✅ [Kaplie] Media permissions granted');
      } catch (permError) {
        console.error('❌ [Kaplie] Permission denied:', permError.name);
        let errorMsg = 'Camera/microphone access denied.';
        if (permError.name === 'NotAllowedError') {
          errorMsg = 'Camera/microphone access denied. Please allow permissions in your browser settings.';
        } else if (permError.name === 'NotFoundError') {
          errorMsg = 'No camera or microphone found. Please connect a device.';
        } else {
          errorMsg = 'Could not access camera/microphone: ' + permError.message;
        }
        setConnectionError(errorMsg);
        toast.error(errorMsg);
        setIsConnecting(false);
        return;
      }
      
      // Step 4: Create Agora RTC client
      console.log('🔧 [Kaplie] Creating Agora RTC client...');
      const rtcClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      // Register event handlers INLINE so they capture rtcClient directly (not stale state)
      rtcClient.on('user-published', async (remoteUser, mediaType) => {
        console.log('📥 [Kaplie] Remote user published:', mediaType);
        await rtcClient.subscribe(remoteUser, mediaType);
        console.log('✅ [Kaplie] Subscribed to remote', mediaType);
        
        if (mediaType === 'video') {
          setRemoteUsers(prev => ({ ...prev, [remoteUser.uid]: remoteUser }));
          setTimeout(() => {
            if (remoteVideoRef.current && remoteUser.videoTrack) {
              remoteUser.videoTrack.play(remoteVideoRef.current);
              console.log('✅ [Kaplie] Remote video playing');
            }
          }, 200);
        }
        
        if (mediaType === 'audio') {
          remoteUser.audioTrack?.play();
          console.log('✅ [Kaplie] Remote audio playing');
        }
      });

      rtcClient.on('user-unpublished', (remoteUser, mediaType) => {
        if (mediaType === 'video') {
          setRemoteUsers(prev => {
            const updated = { ...prev };
            delete updated[remoteUser.uid];
            return updated;
          });
        }
      });

      rtcClient.on('user-left', (remoteUser) => {
        setRemoteUsers(prev => {
          const updated = { ...prev };
          delete updated[remoteUser.uid];
          return updated;
        });
        setTheirCaption({ text: '', words: [], currentWordIndex: -1, speaker: '', isInterim: true, translated: '' });
        toast.info('Other person left');
      });
      rtcClient.on('connection-state-change', (curState, prevState) => {
        console.log(`📶 [Kaplie] Connection state: ${prevState} → ${curState}`);
        if (curState === 'DISCONNECTED') {
          setConnectionError('Connection lost. Please try again.');
        }
      });
      setAgoraClient(rtcClient);
      console.log('✅ [Kaplie] RTC client created');
      
      // Step 5: Join the channel FIRST (before creating tracks)
      console.log('🔗 [Kaplie] Joining channel:', channelName);
      try {
        await rtcClient.join(appId, channelName, agoraToken, 0);
        console.log('✅ [Kaplie] Joined channel successfully');
      } catch (joinError) {
        console.error('❌ [Kaplie] Failed to join channel:', joinError.message);
        const errorMsg = 'Failed to join call: ' + (joinError.message || 'Unknown error');
        setConnectionError(errorMsg);
        toast.error(errorMsg);
        setIsConnecting(false);
        return;
      }
      
      // Step 6: Create audio and video tracks
      console.log('🎥 [Kaplie] Creating media tracks...');
      let audioTrack, videoTrack;
      try {
        audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        console.log('✅ [Kaplie] Audio track created');
        
        videoTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: { width: 640, height: 480, frameRate: 24 }
        });
        console.log('✅ [Kaplie] Video track created');
      } catch (trackError) {
        console.error('❌ [Kaplie] Failed to create tracks:', trackError.message);
        const errorMsg = 'Could not access camera/microphone. Please check permissions.';
        setConnectionError(errorMsg);
        toast.error(errorMsg);
        await rtcClient.leave();
        setIsConnecting(false);
        return;
      }
      
      setLocalAudioTrack(audioTrack);
      setLocalVideoTrack(videoTrack);
      
      // Step 7: Publish tracks
      console.log('📤 [Kaplie] Publishing tracks...');
      try {
        await rtcClient.publish([audioTrack, videoTrack]);
        console.log('✅ [Kaplie] Tracks published successfully');
      } catch (pubError) {
        console.error('❌ [Kaplie] Failed to publish tracks:', pubError.message);
        toast.error('Failed to start video/audio');
      }
      
      // Step 8: Play local video immediately (useEffect is backup)
      console.log('🎥 [Kaplie] Playing local video immediately...');
      if (localVideoRef.current) {
        try {
          videoTrack.play(localVideoRef.current, { fit: 'cover', mirror: true });
          console.log('✅ [Kaplie] Local video started immediately');
        } catch(e) {
          console.warn('⚠️ [Kaplie] Immediate play failed, useEffect will retry');
        }
      }
      
      // Step 9: Connect WebSocket for caption sharing (replaces RTM)
      console.log('💬 [Kaplie] Connecting WebSocket for captions...');
      try {
        const wsUserName = user?.name || 'Guest-' + Date.now();
        const ws = new WebSocket(`${WS_URL}/api/ws/captions/${channelName}/${encodeURIComponent(wsUserName)}`);
        
        ws.onopen = () => {
          console.log('✅ [Kaplie] WebSocket connected for captions');
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'caption' && data.speaker !== wsUserName) {
              handleIncomingCaption(data);
            }
          } catch (e) {
            console.error('WS message parse error:', e);
          }
        };
        
        ws.onclose = () => {
          console.log('💬 [Kaplie] WebSocket closed');
        };
        
        ws.onerror = (e) => {
          console.warn('⚠️ [Kaplie] WebSocket error:', e);
        };
        
        wsRef.current = ws;
      } catch (wsError) {
        console.warn('⚠️ [Kaplie] WebSocket failed:', wsError.message);
      }
      
      // Success!
      // IMPORTANT: Clear connecting state FIRST so main JSX mounts localVideoRef,
      // then set isJoined which triggers the useEffect to play() the video
      setIsConnecting(false);
      setConnectionError(null);
      setIsJoined(true);
      toast.success('Connected!');
      console.log('🎉 [Kaplie] Call fully initialized!');
      
      if (captionsEnabled) {
        startSpeechRecognition();
      }
      
    } catch (error) {
      console.error('❌ [Kaplie] Unexpected error:', error);
      setConnectionError('Connection failed. Please check permissions and try again.');
      toast.error('Connection failed. Please check permissions and try again.');
      setIsConnecting(false);
    }
  };

  // Translate text using backend
  const translateText = async (text, targetLang) => {
    if (!text || targetLang === 'en') return text;
    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.post(`${API}/translate`, {
        text,
        target_language: targetLang
      }, { headers: { Authorization: `Bearer ${token}` } });
      return response.data.translated;
    } catch (e) {
      console.error('Translation failed:', e);
      return text;
    }
  };

  // Handle incoming caption from WebSocket
  const handleIncomingCaption = async (data) => {
    const words = data.text.split(' ').filter(w => w);
    let translated = '';
    
    if (translateEnabledRef.current && myLanguageRef.current !== 'en') {
      translated = await translateText(data.text, myLanguageRef.current);
    }
    
    setTheirCaption({
      text: data.text,
      words,
      currentWordIndex: words.length - 1,
      speaker: data.speaker || 'Them',
      isInterim: !data.is_final,
      translated
    });
    
    if (data.is_final && data.text) {
      if (isSavingRef.current) {
        transcriptBufferRef.current.push({
          speaker: data.speaker || 'Them',
          text: data.text,
          timestamp: new Date().toISOString()
        });
      }
      
      setCaptionHistory(prev => [
        ...prev.slice(-2),
        { text: data.text, speaker: data.speaker, isMine: false, translated }
      ]);
      
      setTimeout(() => {
        setTheirCaption(prev => 
          prev.text === data.text ? { text: '', words: [], currentWordIndex: -1, speaker: '', isInterim: true, translated: '' } : prev
        );
      }, 4000);
    }
  };

  // Send caption via WebSocket
  const sendCaption = useCallback((text, isFinal) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !text) return;
    
    const now = Date.now();
    if (!isFinal && now - lastSentRef.current < 150) return;
    lastSentRef.current = now;
    
    ws.send(JSON.stringify({
      type: 'caption',
      text,
      is_final: isFinal
    }));
  }, []);

  // Speech recognition with word-by-word tracking
  const startSpeechRecognition = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Use Chrome for captions');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onresult = async (event) => {
      let interim = '';
      let final = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final = transcript;
        } else {
          interim = transcript;
        }
      }
      
      if (interim) {
        // Send interim to the other person (don't display locally)
        sendCaption(interim, false);
      }
      
      if (final) {
        // Send final to the other person
        sendCaption(final, true);
        
        // Save to transcript if saving is enabled
        addTranscriptEntry('You', final);
        
        setCaptionHistory(prev => [
          ...prev.slice(-2),
          { text: final, speaker: 'You', isMine: true }
        ]);
      }
    };
    
    recognition.onerror = (event) => {
      if (event.error !== 'no-speech') console.error('Speech error:', event.error);
    };
    
    recognition.onend = () => {
      if (recognitionRef.current && captionsEnabled && !isMuted) {
        try { recognition.start(); } catch (e) {}
      }
    };
    
    recognitionRef.current = recognition;
    recognition.start();
  }, [captionsEnabled, isMuted, sendCaption]);

  const stopSpeechRecognition = () => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  };

  // Video event handlers are now inline in initializeCall() to avoid stale closures

  // Controls
  const toggleMute = async () => {
    if (localAudioTrack) {
      await localAudioTrack.setEnabled(isMuted);
      setIsMuted(!isMuted);
      if (!isMuted) stopSpeechRecognition();
      else if (captionsEnabled) startSpeechRecognition();
    }
  };

  const toggleVideo = async () => {
    if (localVideoTrack) {
      await localVideoTrack.setEnabled(isVideoOff);
      setIsVideoOff(!isVideoOff);
    }
  };

  const toggleCaptions = () => {
    const newState = !captionsEnabled;
    setCaptionsEnabled(newState);
    if (newState && !isMuted) startSpeechRecognition();
    else stopSpeechRecognition();
  };

  const cleanup = async () => {
    stopSpeechRecognition();
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (e) {}
      wsRef.current = null;
    }
    localAudioTrack?.stop();
    localAudioTrack?.close();
    localVideoTrack?.stop();
    localVideoTrack?.close();
    if (agoraClient) await agoraClient.leave();
  };

  const handleEndCall = async () => {
    // If saving, flush remaining transcript and end conversation
    if (isSaving && conversationId) {
      await flushTranscriptBuffer();
      try {
        const token = localStorage.getItem('kaplie_token');
        if (token) {
          await axios.put(`${API}/conversations/${conversationId}/end`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      } catch (e) {
        console.error('Failed to end conversation:', e);
      }
    }
    if (flushTimerRef.current) clearInterval(flushTimerRef.current);
    await cleanup();
    navigate(user ? '/dashboard' : '/');
  };

  // Conversation saving functions
  const flushTranscriptBuffer = async () => {
    if (transcriptBufferRef.current.length === 0 || !conversationId) return;
    const entries = [...transcriptBufferRef.current];
    transcriptBufferRef.current = [];
    try {
      const token = localStorage.getItem('kaplie_token');
      if (token) {
        await axios.put(`${API}/conversations/${conversationId}/entries`, { entries }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (e) {
      console.error('Failed to flush transcript:', e);
      // Put entries back
      transcriptBufferRef.current = [...entries, ...transcriptBufferRef.current];
    }
  };

  const toggleSaveConversation = async () => {
    if (!user) {
      toast.error('Sign in to save conversations');
      return;
    }

    if (isSaving) {
      // Stop saving
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      await flushTranscriptBuffer();
      if (conversationId) {
        try {
          const token = localStorage.getItem('kaplie_token');
          await axios.put(`${API}/conversations/${conversationId}/end`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Conversation saved!');
        } catch (e) {
          console.error('Failed to end conversation:', e);
        }
      }
      setIsSaving(false);
      setConversationId(null);
    } else {
      // Start saving
      try {
        const token = localStorage.getItem('kaplie_token');
        const res = await axios.post(`${API}/conversations`, {
          channel_name: channelName,
          contact_name: hasRemoteUser ? 'Call Participant' : 'Solo Call'
        }, { headers: { Authorization: `Bearer ${token}` } });
        setConversationId(res.data.id);
        setIsSaving(true);
        transcriptBufferRef.current = [];
        // Flush buffer every 10 seconds
        flushTimerRef.current = setInterval(flushTranscriptBuffer, 10000);
        toast.success('Saving conversation');
      } catch (e) {
        toast.error('Failed to start saving');
      }
    }
  };

  // Add transcript entry to buffer when final caption arrives
  const addTranscriptEntry = useCallback((speaker, text) => {
    if (!isSaving) return;
    transcriptBufferRef.current.push({
      speaker,
      text,
      timestamp: new Date().toISOString()
    });
  }, [isSaving]);

  // Share call link via native share or clipboard
  const shareCallLink = async () => {
    const callUrl = `${window.location.origin}/call/${channelName}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join my Kaplie call',
          text: 'Tap to join my video call with live captions',
          url: callUrl,
        });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return; // User cancelled
      }
    }
    
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(callUrl);
      setLinkCopied(true);
      toast.success('Call link copied!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  // Netflix/TikTok Caption Component
  const CinematicCaption = ({ caption, isMine }) => {
    if (!caption.text) return null;
    
    // Only show translation if translateEnabled is ON
    const showTranslation = translateEnabled && caption.translated;
    
    return (
      <div 
        className={`transform transition-all duration-300 ease-out ${
          caption.isInterim ? 'opacity-90' : 'opacity-100'
        }`}
      >
        {/* Speaker label */}
        <p className={`text-xs font-semibold uppercase tracking-widest mb-2 ${
          isMine ? 'text-white/60' : 'text-[#D4AF37]/80'
        }`}>
          {isMine ? 'You' : caption.speaker}
        </p>
        
        {/* Main caption with word highlighting */}
        <div className="flex flex-wrap justify-center gap-x-2 gap-y-1">
          {caption.words.map((word, idx) => (
            <span
              key={idx}
              className={`text-2xl sm:text-3xl md:text-4xl font-bold transition-all duration-150 ${
                idx === caption.currentWordIndex
                  ? isMine 
                    ? 'text-white scale-110 drop-shadow-[0_0_20px_rgba(255,255,255,0.5)]' 
                    : 'text-[#D4AF37] scale-110 drop-shadow-[0_0_20px_rgba(212,175,55,0.5)]'
                  : idx < caption.currentWordIndex
                    ? isMine ? 'text-white/70' : 'text-[#D4AF37]/60'
                    : isMine ? 'text-white/40' : 'text-[#D4AF37]/30'
              }`}
              style={{
                textShadow: idx === caption.currentWordIndex 
                  ? '0 4px 20px rgba(0,0,0,0.8), 0 2px 4px rgba(0,0,0,0.9)'
                  : '0 2px 10px rgba(0,0,0,0.6)'
              }}
            >
              {word}
            </span>
          ))}
        </div>
        
        {/* Translated text - ONLY shown when translation is enabled */}
        {showTranslation && (
          <p className="text-lg text-[#D4AF37]/80 mt-3 italic text-center">
            {caption.translated}
          </p>
        )}
      </div>
    );
  };

  // Loading/Error screen
  if (isConnecting || connectionError) {
    return (
      <div className="h-screen w-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          {connectionError ? (
            <>
              {/* Error State */}
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-white text-lg mb-2">Connection Failed</p>
              <p className="text-white/60 text-sm mb-6">{connectionError}</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => {
                    setConnectionError(null);
                    setIsConnecting(true);
                    initializeCall();
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] rounded-full font-semibold text-black transition-all"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate(user ? '/dashboard' : '/')}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-full font-medium text-white transition-all"
                >
                  Go Back
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Loading State */}
              <div className="w-16 h-16 border-4 border-white/20 border-t-[#D4AF37] rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white text-lg mb-2">Connecting...</p>
              <p className="text-white/40 text-sm">
                Please allow camera and microphone access when prompted
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen bg-black overflow-hidden" data-testid="video-call-page">
      {/* Video Layer */}
      <div className="absolute inset-0">
        {/* Remote Video - always in DOM to prevent React reordering, hidden when unused */}
        <div 
          ref={remoteVideoRef} 
          data-testid="remote-video"
          className={`absolute inset-0 w-full h-full bg-neutral-900 ${hasRemoteUser ? '' : 'hidden'}`}
        />

        {/* Local Video - ALWAYS mounted, repositioned via CSS (never unmounted) */}
        <div 
          data-testid="local-video-wrapper"
          className={`absolute overflow-hidden ${
            hasRemoteUser 
              ? 'top-6 right-6 w-32 h-44 sm:w-40 sm:h-56 rounded-3xl shadow-2xl border border-white/20 z-40'
              : 'inset-0 flex items-center justify-center p-4 z-10'
          }`}
        >
          <div className={`relative overflow-hidden bg-neutral-800 ${
            hasRemoteUser ? 'w-full h-full rounded-3xl' : 'w-full max-w-md rounded-3xl shadow-2xl'
          }`}
          style={!hasRemoteUser ? { aspectRatio: '3/4', minHeight: '400px' } : {}}
          >
            {/* Single stable video container - Agora plays into this div, never destroyed */}
            <div 
              ref={localVideoRef}
              data-testid="local-video"
              className="absolute inset-0 w-full h-full"
            />

            {/* Video off overlay */}
            {isVideoOff && (
              <div className="absolute inset-0 bg-neutral-800 flex items-center justify-center">
                {hasRemoteUser ? (
                  <span className="text-2xl font-semibold text-white">
                    {user?.name?.charAt(0)?.toUpperCase() || 'Y'}
                  </span>
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#C5A059]/10 flex items-center justify-center">
                    <span className="text-4xl font-semibold text-white">
                      {user?.name?.charAt(0)?.toUpperCase() || 'Y'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* "You" badge when alone */}
            {!hasRemoteUser && !isVideoOff && (
              <div className="absolute bottom-4 left-4 bg-black/60 rounded-full px-3 py-1.5">
                <span className="text-white text-sm font-medium">You</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Waiting Message + Share Link */}
      {!hasRemoteUser && isJoined && (
        <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-40 flex flex-col items-center gap-3">
          <div className="bg-white/10 backdrop-blur-xl rounded-full px-6 py-3 border border-white/10">
            <p className="text-white text-sm">Waiting for someone to join...</p>
          </div>
          <button
            onClick={shareCallLink}
            data-testid="share-call-link"
            className="flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] rounded-full px-5 py-2.5 text-black text-sm font-semibold shadow-lg shadow-[#D4AF37]/25 transition-all active:scale-95"
          >
            {linkCopied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
            {linkCopied ? 'Link Copied!' : 'Share Call Link'}
          </button>
        </div>
      )}

      {/* Netflix/TikTok Style Captions */}
      {captionsEnabled && (
        <div className="absolute bottom-36 left-0 right-0 z-40 px-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Only show the OTHER person's captions - not your own */}
            {theirCaption.text && (
              <div className="bg-gradient-to-r from-[#D4AF37]/15 to-[#C5A059]/5 backdrop-blur-xl rounded-2xl px-8 py-6 border border-[#D4AF37]/20">
                <CinematicCaption caption={theirCaption} isMine={false} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Premium Control Bar */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="flex items-center gap-2 p-2 bg-black/40 backdrop-blur-2xl rounded-full border border-white/10">
          {/* Mic */}
          <button onClick={toggleMute} data-testid="toggle-mic" className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isMuted ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'}`}>
            {isMuted ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-white" />}
          </button>

          {/* Video */}
          <button onClick={toggleVideo} data-testid="toggle-video" className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isVideoOff ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'}`}>
            {isVideoOff ? <VideoOff className="w-5 h-5 text-white" /> : <VideoIcon className="w-5 h-5 text-white" />}
          </button>

          {/* Captions */}
          <button onClick={toggleCaptions} data-testid="toggle-captions" className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${captionsEnabled ? 'bg-green-500' : 'bg-white/20 hover:bg-white/30'}`}>
            {captionsEnabled ? <MessageSquare className="w-5 h-5 text-white" /> : <MessageSquareOff className="w-5 h-5 text-white" />}
          </button>

          {/* Translate */}
          <button 
            onClick={() => setShowLanguageModal(true)} 
            data-testid="toggle-translate"
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${translateEnabled ? 'bg-blue-500' : 'bg-white/20 hover:bg-white/30'}`}
          >
            <Globe className="w-5 h-5 text-white" />
          </button>

          {/* Save Conversation */}
          {user && (
            <button 
              onClick={toggleSaveConversation}
              data-testid="toggle-save"
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isSaving ? 'bg-green-500' : 'bg-white/20 hover:bg-white/30'}`}
            >
              {isSaving ? <BookmarkCheck className="w-5 h-5 text-white" /> : <BookmarkPlus className="w-5 h-5 text-white" />}
            </button>
          )}

          {/* End Call */}
          <button onClick={handleEndCall} data-testid="end-call" className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all">
            <PhoneOff className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Saving Indicator */}
      {isSaving && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center gap-2 bg-green-500/90 rounded-full px-4 py-2">
            <BookmarkCheck className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">Saving Conversation</span>
          </div>
        </div>
      )}

      {/* Translation Indicator */}
      {translateEnabled && !isSaving && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center gap-2 bg-blue-500/90 rounded-full px-4 py-2">
            <Globe className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">
              Translating to {LANGUAGES.find(l => l.code === myLanguage)?.name}
            </span>
          </div>
        </div>
      )}

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]">
          <div className="bg-neutral-900 rounded-3xl p-6 w-full max-w-sm mx-4 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Translation</h3>
              <button onClick={() => setShowLanguageModal(false)} className="p-2 hover:bg-white/10 rounded-full">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {/* Toggle with clear state */}
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Translation</p>
                  <p className={`text-sm ${translateEnabled ? 'text-green-400' : 'text-white/40'}`}>
                    {translateEnabled ? 'ON' : 'OFF'}
                  </p>
                </div>
                <button
                  onClick={() => setTranslateEnabled(!translateEnabled)}
                  className={`w-16 h-9 rounded-full transition-all flex items-center px-1 ${
                    translateEnabled ? 'bg-green-500' : 'bg-white/20'
                  }`}
                >
                  <div className={`w-7 h-7 bg-white rounded-full shadow-lg transition-all ${
                    translateEnabled ? 'translate-x-7' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            </div>
            
            {/* Language selector - only show when enabled */}
            {translateEnabled && (
              <div className="mb-6 animate-fade-in">
                <label className="text-sm text-white/60 mb-2 block">Translate captions to:</label>
                <select 
                  value={myLanguage}
                  onChange={(e) => setMyLanguage(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                >
                  {LANGUAGES.filter(l => l.code !== 'en').map(lang => (
                    <option key={lang.code} value={lang.code} className="bg-neutral-900">
                      {lang.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-white/40 mt-2">
                  Original text will show on top, translation below
                </p>
              </div>
            )}
            
            <button
              onClick={() => setShowLanguageModal(false)}
              className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] rounded-xl font-semibold text-black hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] transition-all"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Channel Badge + Guest Count */}
      <div className="absolute top-6 left-6 z-40 flex items-center gap-2">
        <div className="bg-black/40 backdrop-blur-xl rounded-full px-4 py-2 border border-white/10">
          <p className="text-white text-sm">{channelName}</p>
        </div>
        {user && (
          <div className="bg-black/40 backdrop-blur-xl rounded-full px-3 py-2 border border-white/10">
            <p className="text-white/70 text-xs">
              <span className="text-white font-medium">{Object.keys(remoteUsers).length}</span>
              /{planInfo.max_guests} Guests
            </p>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]">
          <div className="bg-neutral-900 rounded-3xl p-6 w-full max-w-sm mx-4 border border-white/10">
            <h3 className="text-xl font-bold text-white mb-2">Upgrade to Host More Guests</h3>
            <p className="text-white/60 text-sm mb-6">
              Your current plan allows {planInfo.max_guests} guest{planInfo.max_guests !== 1 ? 's' : ''} per call. Upgrade to Pro to host up to 5 guests and unlock group conversations.
            </p>
            <div className="space-y-3">
              <a href="/subscribe" className="block">
                <button className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] rounded-xl font-semibold text-black hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] transition-all">
                  Upgrade to $17.99/mo
                </button>
              </a>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="w-full py-3 bg-white/10 rounded-xl font-semibold text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCallPage;
